/**
 * Centralized decision predicates for the v2 routing utility.
 *
 * Every per-pipeline business rule (master vs. daily, past-date branch,
 * on-request vs. recurring) lives here as a pure predicate. Pipeline
 * builders and the executor read these instead of re-deriving the same
 * truth tables — no scattered `if (includeMaster && cseIds.length > 0)`
 * chains.
 */

import { partition } from 'lodash'

import type { ServiceDate } from 'types/entities/shared/ids'
import type { DailyStop, MasterStop, OnRequestStop, Phase, RouteOpIntent, Stop } from 'types/modules/routing'

const assertNever = (x: never): never => {
  throw new Error(`Unhandled discriminant: ${JSON.stringify(x)}`)
}

/**
 * `true` only when the intent is allowed to (and intends to) touch the master
 * sequence + future dailies.
 *
 * - `unassign` always returns `true`. The
 *   `project_master_sequence_decoupling` memory pins this: removing a stop
 *   should naturally propagate so it doesn't reappear on future days. The
 *   `includeMaster` flag on `UnassignIntent` is therefore advisory and ignored
 *   here — daily-only unassign is not an option the utility offers.
 * - `assign` / `move` / `resequence` opt in via `includeMaster: true`.
 * - `change-day-of-week` is master-only by definition, so it is always `true`.
 * - `change-date` has no `includeMaster` field — it is daily-only by
 *   construction (the master variant is `change-day-of-week`).
 */
export const shouldIncludeMaster = (intent: RouteOpIntent): boolean => {
  switch (intent.kind) {
    case 'unassign':
      // Defaults to `true` (preserves the historical "permanent unassign"
      // behaviour from `useRouteStopOperations.unassignStops`) but honours
      // an explicit `false` so the bulk dialog's "Save current day" path can
      // request a daily-only unassign without going around the utility.
      return intent.includeMaster !== false
    case 'change-day-of-week':
      return true
    case 'assign':
    case 'move':
    case 'resequence':
      return intent.includeMaster === true
    case 'complete':
      // Completion is a daily-only status change — never touches master.
      return false
    case 'change-date':
      // Defaults to `false` (daily-only "move between days"). When the caller
      // explicitly opts in (`includeMaster: true`), the strategy emits the
      // atomic master+daily `bulkChangeDate` shape — pre-routeOps
      // `BulkPermanentRecurrenceChangeDialog` Feb 2024 path.
      return intent.includeMaster === true
    case 'master-publish':
    case 'restore-master-version':
    case 'sync-daily-to-master':
      // Always touches master by definition.
      return true
    case 'restore-daily-version':
      return false
    default:
      return assertNever(intent)
  }
}

/**
 * `true` when `serviceDate` falls strictly before today (local midnight).
 *
 * Mirrors the v1 helper at `pipelines.js:86-93`. The local-midnight comparison
 * is intentional: the BE's `findAllFutureStopsByCse` filter cuts on
 * `serviceDate >= today` in JVM-default TZ, and the master cascade only flips
 * dates from "today onward" — so the FE has to agree on what "today" is.
 *
 * Returns `false` for invalid `Date` inputs (NaN time, non-`Date` `now`). A
 * "not-past" default is the safer fallback: it leaves the master cascade
 * eligible to run; a wrong `true` here would silently strip the cascade and
 * mask data drift.
 */
export const isPastServiceDate = (serviceDate: ServiceDate, now: Date = new Date()): boolean => {
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return false
  const parsed = new Date(serviceDate)
  if (Number.isNaN(parsed.getTime())) return false
  const today = new Date(now)
  today.setHours(0, 0, 0, 0)
  parsed.setHours(0, 0, 0, 0)
  return parsed.getTime() < today.getTime()
}

/**
 * Type guard for `OnRequestStop`.
 *
 * On-request stops have `cseId: null` and `kind: 'on-request'` — the
 * discriminator makes "this stop has no CSE row" structurally provable, so
 * downstream master-pipeline code can't accidentally pick one up.
 */
export const isOnRequest = (stop: Stop): stop is OnRequestStop => stop.kind === 'on-request'

/**
 * Split a mixed daily / on-request list into its two homogeneous halves.
 *
 * On-request stops have to be partitioned out of resequence calculations and
 * re-injected at their original positions. Lodash `partition`
 * keeps that one-liner readable; the cast preserves the narrowed types so
 * callers don't have to re-narrow.
 */
export const partitionTemporary = <T extends DailyStop | OnRequestStop>(
  stops: ReadonlyArray<T>
): {
  readonly recurring: ReadonlyArray<DailyStop>
  readonly temporary: ReadonlyArray<OnRequestStop>
} => {
  const [temporary, recurring] = partition(stops, isOnRequest)
  return {
    recurring: recurring as ReadonlyArray<DailyStop>,
    temporary: temporary as ReadonlyArray<OnRequestStop>,
  }
}

/**
 * `true` when the op's planned phases include one that mutates state
 * affecting *future* dailies (the master cascade fan-out the executor needs
 * to schedule + verify).
 *
 * Drives the executor's "should I run the master-cascade phase?" decision —
 * keeps that question in one place instead of re-derived per builder.
 */
export const requiresMasterCascade = (intent: RouteOpIntent): boolean => {
  switch (intent.kind) {
    case 'unassign':
      return intent.includeMaster !== false
    case 'change-day-of-week':
      return true
    case 'assign':
    case 'move':
      return intent.includeMaster === true
    case 'resequence':
    case 'change-date':
    case 'complete':
      // resequence is a re-ordering of stops already on the route; no
      // structural cascade. change-date and complete never touch master by
      // design.
      return false
    case 'master-publish':
      // The pipeline carries explicit per-date daily payloads, so the future
      // cascade is unnecessary. Returning false keeps `getDailyOpsScope`'s
      // `runFutureCascade` flag accurate.
      return false
    case 'restore-master-version':
    case 'restore-daily-version':
    case 'sync-daily-to-master':
      // Single atomic BE call — the server handles any daily fanout. No
      // FE-side cascade phase.
      return false
    default:
      return assertNever(intent)
  }
}

/**
 * Single decision tree mapping an intent's `(includeMaster, isPast)` pair to
 * the scope of the daily-side pipeline. Replaces the scattered
 * `if (!includeMaster) ... else if (isPast) ... else ...` branches in v1
 * (`pipelines.js:133-148`).
 *
 * - `runMasterFirst` — emit master phases (assign/unassign/resequence) before daily.
 *   Note: even when this is true the master phase actually executes after the
 *   source-daily unassign (`UNASSIGN_DAILY → UNASSIGN_MASTER`); the flag means
 *   "the master-side phases are part of this op," not "they fire literally first."
 * - `runDailyOnly` — only the daily phase fires (master + future cascade skipped).
 *   True when the intent is daily-scoped *or* when the target date is in the
 *   past (master cascade BE-side only flips `serviceDate >= today`).
 * - `runFutureCascade` — emit the future-daily cascade phase
 *   (`ASSIGN_FUTURE_DAILY` / `UNASSIGN_FUTURE_DAILY`). Suppressed for past
 *   dates because there are no future occurrences to update.
 *
 * No `cseIds` parameter — the executor decides at run-time whether to skip a
 * cascade phase that would have zero work (e.g. a master pipeline with 0
 * CSE-eligible stops). Keeping that runtime check out of the decision layer
 * is intentional: the scope is a *contract*, not a payload-shape gate.
 */
export const getDailyOpsScope = (
  intent: RouteOpIntent,
  now: Date = new Date()
): {
  readonly runMasterFirst: boolean
  readonly runDailyOnly: boolean
  readonly runFutureCascade: boolean
} => {
  const includeMaster = shouldIncludeMaster(intent)
  const cascade = requiresMasterCascade(intent)

  // change-day-of-week is master-only — it has no daily-only fallback and no
  // serviceDate to gate on. Fast-path so the past-date branch below doesn't
  // mis-fire on the optional `sourceServiceDate`.
  if (intent.kind === 'change-day-of-week') {
    return { runMasterFirst: true, runDailyOnly: false, runFutureCascade: true }
  }

  // master-publish carries per-date daily payloads, no single anchor
  // serviceDate. Fast-path with the publish-pipeline contract: master + daily
  // both fire, no future cascade (the FE supplies the per-date lists).
  if (intent.kind === 'master-publish') {
    return { runMasterFirst: true, runDailyOnly: false, runFutureCascade: false }
  }

  // Single atomic BE call — server handles any daily fanout, no anchor
  // serviceDate on the intent.
  if (intent.kind === 'restore-master-version' || intent.kind === 'restore-daily-version' || intent.kind === 'sync-daily-to-master') {
    return { runMasterFirst: true, runDailyOnly: false, runFutureCascade: false }
  }

  // Pull the date being mutated. Each daily-bearing intent kind names the
  // field it operates against; an intent without one (only change-day-of-week,
  // already handled above) returns `undefined` so the past-date check no-ops.
  const dateForGate: ServiceDate | undefined = intent.kind === 'change-date' ? intent.fromServiceDate : intent.serviceDate

  const past = dateForGate ? isPastServiceDate(dateForGate, now) : false

  if (!includeMaster) {
    return { runMasterFirst: false, runDailyOnly: true, runFutureCascade: false }
  }

  // Past date + master opt-in: the BE cascade no-ops because
  // `findAllFutureStopsByCse` filters `serviceDate >= today`. We still run
  // the daily phase alone so the user's selected day is updated; future
  // cascade is structurally pointless.
  if (past) {
    return { runMasterFirst: false, runDailyOnly: true, runFutureCascade: false }
  }

  return { runMasterFirst: true, runDailyOnly: false, runFutureCascade: cascade }
}

/**
 * Type guard: `true` for stops that *can* drive a master mutation.
 *
 * `OnRequestStop` is rejected at the type level — it has `cseId: null`, so a
 * master cascade is structurally impossible (BE `unassignDailyByCse` would
 * be a no-op). Master and daily stops both carry a `cseId`, so they pass.
 *
 * Used by builders to filter selected stops before emitting master phases.
 */
export const canMutateMaster = (stop: Stop): stop is MasterStop | DailyStop => stop.kind === 'master' || stop.kind === 'daily'

/**
 * Canonical phase ordering per intent kind. Builders consume this instead of
 * hand-rolling `steps.push(...)` sequences — keeps the "no parallelism
 * between source and destination" promise testable as a single array.
 *
 * `RESEQUENCE_DAILY` is included even when an intent doesn't carry an
 * explicit ordered list — the executor decides at run-time whether to skip
 * the step (e.g. when there's nothing to resequence). The decision layer
 * commits to the *order*, not the *presence*.
 */
export const getMutationOrder = (intent: RouteOpIntent): ReadonlyArray<Phase> => {
  switch (intent.kind) {
    case 'unassign':
      // includeMaster decides whether master + future-daily fire; ordering
      // when both fire is daily-first so the BE cascade can't race a stale
      // master row.
      return shouldIncludeMaster(intent)
        ? ['UNASSIGN_DAILY', 'UNASSIGN_MASTER', 'UNASSIGN_FUTURE_DAILY', 'VERIFY']
        : ['UNASSIGN_DAILY', 'VERIFY']

    case 'assign':
      return shouldIncludeMaster(intent)
        ? ['ASSIGN_DAILY', 'RESEQUENCE_DAILY', 'ASSIGN_MASTER', 'ASSIGN_FUTURE_DAILY', 'RESEQUENCE_MASTER', 'VERIFY']
        : ['ASSIGN_DAILY', 'RESEQUENCE_DAILY', 'VERIFY']

    case 'move':
      // strictly sequential, source-then-destination, no parallelism.
      return shouldIncludeMaster(intent)
        ? [
            'UNASSIGN_DAILY',
            'ASSIGN_DAILY',
            'RESEQUENCE_DAILY',
            'UNASSIGN_MASTER',
            'ASSIGN_MASTER',
            'ASSIGN_FUTURE_DAILY',
            'RESEQUENCE_MASTER',
            'VERIFY',
          ]
        : ['UNASSIGN_DAILY', 'ASSIGN_DAILY', 'RESEQUENCE_DAILY', 'VERIFY']

    case 'resequence':
      return shouldIncludeMaster(intent) ? ['RESEQUENCE_DAILY', 'RESEQUENCE_MASTER', 'VERIFY'] : ['RESEQUENCE_DAILY', 'VERIFY']

    case 'complete':
      return ['BULK_COMPLETE', 'VERIFY']

    case 'change-date':
      // ValidateNewStopsDaily requires `wo.service_date` to match the target
      // before assign — so change-date-daily fires first, then re-assign +
      // resequence on the target route. (pipelines.js:919.)
      return ['CHANGE_DATE_DAILY', 'ASSIGN_DAILY', 'RESEQUENCE_DAILY', 'VERIFY']

    case 'change-day-of-week':
      return ['CHANGE_DAY_MASTER', 'ASSIGN_MASTER', 'RESEQUENCE_MASTER', 'VERIFY']

    case 'master-publish':
      // Mirrors the legacy `executePublishPipeline` order — daily-side
      // unassign first so a stale master row can't strand daily entries,
      // then master assign, then per-date daily assigns, then full
      // resequence (master then per-date daily).
      return ['UNASSIGN_DAILY', 'UNASSIGN_MASTER', 'ASSIGN_MASTER', 'ASSIGN_DAILY', 'RESEQUENCE_MASTER', 'RESEQUENCE_DAILY', 'VERIFY']

    case 'restore-master-version':
      return ['RESTORE_MASTER_VERSION', 'VERIFY']

    case 'restore-daily-version':
      return ['RESTORE_DAILY_VERSION', 'VERIFY']

    case 'sync-daily-to-master':
      return ['SYNC_DAILY_TO_MASTER', 'VERIFY']

    default:
      return assertNever(intent)
  }
}
